import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Checkbox } from '@Components/FormCheck';
import { Design, Size } from '@Components/designConstants';
import {
  awaitJSON,
  SheetsLambdaFetch,
  StreamingV2LambdaFetch,
  DagLambdaFetch,
  CreativeLambdaFetch,
  fetchPresigned
} from '../../../utils/fetch-utils';
import { DerivedNetwork } from '../../../redux/networks';
import { AdOpsToolProps, isS3Redirect } from '../adops-constants';

const getBackfillErrorMessage = (
  company: string | undefined,
  network: string,
  dates: DateRange | undefined,
  isCreativeBackfill: boolean,
  creativeArr: string[]
): string => {
  if (!company) {
    return 'Select a company.';
  }
  if (!dates) {
    return 'Select a date range.';
  }
  if (!network) {
    return 'Select a derived id.';
  }
  if (isCreativeBackfill && !creativeArr.length) {
    return 'Select at least one isci.';
  }
  return 'We are unable to diagnose the cause of your error. Please reach out to @app-eng-triage in the #ask-blisspoint channel!';
};

export const FlashtalkingBackfill = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const { setError } = utils;

  const [dates, setDates] = useState<DateRange>();

  const [company, setCompany] = useState<string>();
  const [companyOptions, setCompanyOptions] = useState<string[]>();

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  const [network, setNetwork] = useState('');
  const [derivedNetworkMap, setDerivedNetworkMap] = useState<Record<string, DerivedNetwork>>({});

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const lambdaRes = await StreamingV2LambdaFetch('/derived', { params: { company } });
          const lambdaResult = await awaitJSON<Record<string, DerivedNetwork>>(lambdaRes);
          if (isS3Redirect(lambdaResult)) {
            const S3Res = await fetchPresigned(
              lambdaResult.url,
              'Derived networks download failed'
            );
            const derivedNetworks = await awaitJSON<Record<string, DerivedNetwork>>(S3Res);
            setDerivedNetworkMap(derivedNetworks);
          } else {
            setDerivedNetworkMap(lambdaResult);
          }
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, setError]);

  const derivedIdOptions = useMemo(() => Object.keys(derivedNetworkMap), [derivedNetworkMap]);

  const [isCreativeBackfill, setIsCreativeBackfill] = useState(false);
  const [creativeArr, setCreativeArr] = useState<string[]>([]);
  const [isciRes, setIsciRes] = useState<Record<string, { avail: string }>>({});

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const res = await CreativeLambdaFetch('/', {
            params: {
              company,
              include_retired: true
            }
          });
          setIsciRes(await awaitJSON<Record<string, { avail: string }>>(res));
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, setError]);

  const isciOptionList = useMemo(
    () => Object.keys(isciRes).filter(isci => isciRes[isci].avail === 'N'),
    [isciRes]
  );

  const validateExtra = useCallback(
    () =>
      Boolean(company && network && dates != null && (!isCreativeBackfill || creativeArr.length)),
    [company, network, dates, isCreativeBackfill, creativeArr]
  );

  const backfillFlashtalkingImpressions = useCallback(async () => {
    if (!validateExtra() || !dates) {
      setError({
        message: getBackfillErrorMessage(company, network, dates, isCreativeBackfill, creativeArr)
      });
      return;
    }
    try {
      setActiveAPICall(true);
      await DagLambdaFetch('/start_single_job', {
        method: 'POST',
        body: {
          dag: 'flashtalking-impressions-backfill',
          task: 'FLASHTALKING-IMPRESSIONS-BACKFILL',
          task_name: 'FLASHTALKING-IMPRESSIONS-BACKFILL',
          override: true,
          extra: `${company}_${network}_${dates.start}_${dates.end}${
            isCreativeBackfill ? `_${creativeArr.join(',')}` : ''
          }`
        }
      });
      setCompany(undefined);
      setDates(undefined);
      setNetwork('');
      setIsCreativeBackfill(false);
      setCreativeArr([]);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    } finally {
      setActiveAPICall(false);
    }
  }, [network, company, setError, dates, validateExtra, creativeArr, isCreativeBackfill]);

  return (
    <Card className='ad-ops-card'>
      <div className='ad-ops-card-title'>Backfill Flashtalking Impressions</div>
      <DatePicker
        range={dates}
        design={Design.NEUTRAL}
        isOutsideRange={() => false}
        onChange={range => setDates(range)}
      />
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'
        menuPortalTarget={document.body}
        placeholder='Select from existing companies'
        searchable
        clearable
        options={companyOptions ?? []}
        value={company ?? null}
        onChange={value => {
          setCompany(value ?? undefined);
          setNetwork('');
          setCreativeArr([]);
          setIsCreativeBackfill(false);
        }}
      />
      {company && (
        <Dropdown
          size={Size.LG}
          className='ad-ops-elem'
          menuPortalTarget={document.body}
          placeholder='Select Derived ID'
          searchable
          clearable
          options={derivedIdOptions}
          value={network || null}
          onChange={value => setNetwork(value ?? '')}
        />
      )}
      {company && (
        <div className='check-box-container'>
          <Checkbox
            id='creativeBackfillCheckBox'
            className='ad-ops-elem'
            label='Backfill Only a Specific Creative'
            checked={isCreativeBackfill}
            onChange={() => {
              if (isCreativeBackfill) {
                setCreativeArr([]);
              }
              setIsCreativeBackfill(!isCreativeBackfill);
            }}
          />
        </div>
      )}
      {company && isCreativeBackfill && (
        <Dropdown
          size={Size.LG}
          className='ad-ops-elem'
          menuPortalTarget={document.body}
          placeholder='Select ISCI'
          searchable
          multiSelect
          clearable
          showChips
          options={isciOptionList}
          value={creativeArr}
          onChange={setCreativeArr}
        />
      )}
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={backfillFlashtalkingImpressions}
      >
        Submit
      </Button>
    </Card>
  );
};
